import { request } from '@core/api';
import { errorBox } from '@core/components/AlertBox';
import { mapToUpdateT } from '@core/redux/factory/reducer';
import type { AppDispatch } from '@core/redux/store';
import type { Vector } from '@core/types';
import { processingResultActions } from '@processingResults/redux';
import type { ProcessingResult } from '@processingResults/types';
import type { Update } from '@reduxjs/toolkit';
import { transformTrenchPointsWithScanState } from '@scans/components/ScanDetailPanel/state';
import { scanActions } from '@scans/redux';
import type { Scan } from '@scans/types';
import { pointActions } from '@trenches/redux';
import type { Point } from '@trenches/types';
import { eulerToJson, vectorToJson } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { Euler, Quaternion, Scene, Vector3 } from 'three';
import { TransformationReason } from '@processingResults/components/ProcessingResultsList';
import type { RenderedScanData } from './Scans';

/**
 * Normalizes a vector by treating zero vectors as null.
 */
export const normalizeVector = (
  v: { x: number; y: number; z: number } | null | undefined,
): { x: number; y: number; z: number } | null => {
  if (v === null || v === undefined) return null;
  if (v.x === 0 && v.y === 0 && v.z === 0) return null;
  return v;
};

/**
 * Checks if two vectors are equal (have the same coordinates).
 * Zero vectors are treated as null.
 */
export const vectorsAreEqual = (
  a: { x: number; y: number; z: number } | null,
  b: { x: number; y: number; z: number } | null,
): boolean => {
  const normalizedA = normalizeVector(a);
  const normalizedB = normalizeVector(b);
  if (normalizedA === null && normalizedB === null) return true;
  if (normalizedA === null || normalizedB === null) return false;
  return (
    normalizedA.x === normalizedB.x &&
    normalizedA.y === normalizedB.y &&
    normalizedA.z === normalizedB.z
  );
};

/**
 * Checks if processing result has user transformation data that differs from auto-aligned and original offset.
 * If user transformation equals auto-aligned transformation or original offset (with zero rotation), it means there's no manual transformation.
 * Zero vectors are treated as null (no transformation).
 */
export const hasUserTransformationData = (processingResult?: ProcessingResult): boolean => {
  if (!processingResult) return false;

  const originalOffset = processingResult.texturedMeshes?.[0]?.mesh?.offset;
  const originalTranslation = normalizeVector(originalOffset);

  const userTranslation = normalizeVector(processingResult.userTranslation);
  const userRotation = normalizeVector(processingResult.userRotation);
  const autoTranslation = normalizeVector(processingResult.autoAlignmentTranslation);
  const autoRotation = normalizeVector(processingResult.autoAlignmentRotation);

  const hasUserTranslation = userTranslation !== null;
  const hasUserRotation = userRotation !== null;

  if (!hasUserTranslation && !hasUserRotation) return false;

  const translationEqualsOriginal =
    hasUserTranslation && originalTranslation !== null
      ? vectorsAreEqual(userTranslation, originalTranslation)
      : false;

  const translationEqualsAuto =
    hasUserTranslation && autoTranslation !== null
      ? vectorsAreEqual(userTranslation, autoTranslation)
      : false;

  const rotationEqualsAuto =
    hasUserRotation && autoRotation !== null ? vectorsAreEqual(userRotation, autoRotation) : false;

  if (translationEqualsOriginal && !hasUserRotation) {
    return false;
  }

  if (autoTranslation !== null || autoRotation !== null) {
    const translationDiffers = hasUserTranslation && !translationEqualsAuto;
    const rotationDiffers = hasUserRotation && !rotationEqualsAuto;
    return translationDiffers || rotationDiffers;
  }

  const translationDiffersFromOriginal = hasUserTranslation && !translationEqualsOriginal;
  const rotationDiffersFromOriginal = hasUserRotation;

  return translationDiffersFromOriginal || rotationDiffersFromOriginal;
};

// https://stackoverflow.com/questions/62457529/how-do-you-get-the-axis-and-angle-representation-of-a-quaternion-in-three-js
export function getAxisAndAngleFromQuaternion(q: Quaternion): { axis: Vector3; angle: number } {
  const angle = 2 * Math.acos(q.w);
  let s;
  if (1 - q.w * q.w < 0.000001) {
    // test to avoid divide by zero, s is always positive due to sqrt
    // if s close to zero then direction of axis not important
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/quaternionToAngle/
    s = 1;
  } else {
    s = Math.sqrt(1 - q.w * q.w);
  }
  return { axis: new Vector3(q.x / s, q.y / s, q.z / s), angle };
}

export function createUndoTransformationRequestBody({
  scene,
  selectedScan,
  userTransformedScan,
}: {
  scene: Scene;
  selectedScan: Scan;
  userTransformedScan: RenderedScanData;
}): ScanTransformRequest | null {
  // renderedScan is the originalProcessingResult scan, the scan that shows up when the checkbox use user transformation is off
  const renderedScan = scene.getObjectByName(`${Object3dNames.Scan}:${selectedScan?.id}`);
  const renderedScanParent = renderedScan?.parent;
  if (!renderedScanParent) {
    return null;
  }

  const worldPosition = renderedScanParent.position.clone();
  const worldPositionStart = userTransformedScan?.position;
  if (!worldPositionStart) {
    return null;
  }

  const userTranslation = renderedScanParent.position.clone().add(renderedScan.position.clone());
  const endQuaternion = renderedScanParent.quaternion.clone();
  const startQuaternion = userTransformedScan.quaternion.clone().invert();
  const deltaQuaternion = endQuaternion.multiply(startQuaternion);
  const { axis, angle } = getAxisAndAngleFromQuaternion(deltaQuaternion);

  const processingResult = {
    userTranslation: vectorToJson(userTranslation),
    userRotation: eulerToJson(new Euler()),
  };

  const points = {
    userTranslation: vectorToJson(worldPosition),
    userRotation: {
      angle,
      axis: vectorToJson(axis),
    },
    transformOrigin: vectorToJson(worldPositionStart),
  };

  return transformTrenchPointsWithScanState.applyTransformation
    ? { processingResult, points }
    : { processingResult };
}

export interface ScanTransformRequest {
  processingResult: Partial<ProcessingResult>;
  points?: {
    userTranslation: Vector;
    userRotation: {
      angle: number;
      axis: Vector;
    };
    transformOrigin: Vector;
  };
  comment?: string;
  reason?: TransformationReason;
}

interface ScanTransformResponse {
  processingResult: ProcessingResult;
  points?: Update<Point, string>[];
}

interface RawScanTransformResponse {
  processingResult: ProcessingResult;
  points?: Point[];
}

export const postTransformScan = async (
  dispatch: AppDispatch,
  scanId: string,
  projectId: string,
  processingResultId: string,
  body: ScanTransformRequest,
): Promise<void> => {
  try {
    const { processingResult, points = [] as Update<Point, string>[] } = await request<
      ScanTransformResponse,
      ScanTransformRequest,
      RawScanTransformResponse
    >(
      'POST',
      {
        path: ['projects', projectId, 'processingResults', processingResultId, 'transform'],
        body,
      },
      (res) => ({
        processingResult: res.processingResult,
        points: res.points ? mapToUpdateT(res.points) : [],
      }),
    );
    await dispatch(pointActions.updateMany(points));
    await dispatch(processingResultActions.updateOne(processingResult));
  } catch (_e) {
    errorBox(
      "Could not transform trench points together with scan. Don't worry, neither scan nor points will be transformed.",
    );
  }

  if (!scanId) return;

  // Update scan state based on the transformation reason that was applied
  const isAutoAligned = body.reason === TransformationReason.AutoAligned;
  const isOriginal = body.reason === TransformationReason.Unspecified;

  dispatch(
    scanActions.updateOne({
      id: scanId,
      disableUserTransformation: isAutoAligned || isOriginal,
      disableAutoAlignedPosition: !isAutoAligned,
    }),
  );
};
